import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorDisplay } from "./ErrorDisplay";
import { FallbackError } from "./FallbackError";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId?: string;
  fallbackError?: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      errorId: undefined,
      fallbackError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Generate a unique error ID for tracking
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
      fallbackError: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    // Example: logErrorToService(error, errorInfo, this.state.errorId);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorId: undefined,
      fallbackError: false,
    });
  };

  handleFallbackError = () => {
    // If the main ErrorDisplay fails, use the fallback
    this.setState({
      fallbackError: true,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use fallback error component if main one failed
      if (this.state.fallbackError) {
        return (
          <FallbackError
            error={this.state.error}
            errorId={this.state.errorId}
            onRetry={this.handleRetry}
          />
        );
      }

      // Try to render the main error component
      try {
        return (
          <ErrorDisplay
            error={this.state.error}
            errorId={this.state.errorId}
            onRetry={this.handleRetry}
          />
        );
      } catch (fallbackError) {
        // If ErrorDisplay fails, use the fallback
        console.error("ErrorDisplay failed, using fallback:", fallbackError);
        return (
          <FallbackError
            error={this.state.error}
            errorId={this.state.errorId}
            onRetry={this.handleRetry}
          />
        );
      }
    }

    return this.props.children;
  }
}
